import React from 'react';
import 'twin.macro';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { Spinner } from 'components/Spinner';
import { SelectionResultPanel } from './SelectionResultPanel';
import { useAIGenerationContext } from 'components/Editor/AIAssist/contexts/AIGenerationContext';
import { trackEvent } from 'queries/user/track';

interface Props {
  isLoading: boolean;
  pdfUrl?: string;
  onDownload: () => void;
  onAddAttachment: () => void;
  onCancel: () => void;
  hasAttachmentOption: boolean;
}

export function SelectionPdfResult({
  isLoading,
  pdfUrl,
  onDownload,
  onAddAttachment,
  onCancel,
  hasAttachmentOption,
}: Props) {
  const { t } = useTranslation(['common']);

  let trackingContext: string | undefined;
  try {
    const context = useAIGenerationContext();
    trackingContext = context.trackingContext;
  } catch {
    // Context not available, tracking will be skipped
  }

  const isReady = !isLoading && pdfUrl;
  const actionDisabled = isLoading || !pdfUrl;

  return (
    <SelectionResultPanel
      title={t('common:editor.pdfConversion')}
      footer={
        <>
          <Button
            onClick={() => {
              if (trackingContext) {
                trackEvent('Editor:ExportPDF:cancel', {
                  context: trackingContext,
                });
              }
              onCancel();
            }}
            minimal
          >
            {t('common:buttons.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (trackingContext) {
                trackEvent('Editor:ExportPDF:download', {
                  context: trackingContext,
                });
              }
              onDownload();
            }}
            outlined
            disabled={actionDisabled}
          >
            {t('common:buttons.download')}
          </Button>
          {hasAttachmentOption && (
            <Button
              onClick={() => {
                if (trackingContext) {
                  trackEvent('Editor:ExportPDF:addAttachment', {
                    context: trackingContext,
                  });
                }
                onAddAttachment();
              }}
              intent="primary"
              disabled={actionDisabled}
            >
              {t('common:buttons.addAttachment')}
            </Button>
          )}
        </>
      }
    >
      <div tw="p-4 flex flex-col items-center justify-center gap-2">
        {isLoading && (
          <>
            <Spinner size="2.5rem" tw="text-brand-intense" />
            <p tw="text-neutral-moderate font-bold">
              {t('common:editor.pdfGenerating')}
            </p>
          </>
        )}
        {isReady && (
          <>
            <CheckCircleIcon tw="w-6 h-6 text-neutral-moderate" />
            <p tw="text-neutral-moderate font-bold">
              {t('common:editor.pdfReady')}
            </p>
          </>
        )}
      </div>
    </SelectionResultPanel>
  );
}
